import { type ProspectTaskDto, type TaskPriority } from "@crowdcoursing/api/openapi";
import { DateTime } from "luxon";

import { PRIORITY_LABELS } from "~utils/constants/taskPriorities";

export type TaskFilter = "all" | "open" | "completed";

export const getPriorityIcon = (priority: TaskPriority) => {
  return priority === 4 ? "flag-outlined" : "flag";
};

export const getPriorityLabel = (priority: TaskPriority) => PRIORITY_LABELS[priority];

export const getDueDateColor = (dueAt: string) => {
  const hours = DateTime.fromISO(dueAt).diff(DateTime.now(), "hours").hours;
  if (hours <= 2) {
    return { backgroundColor: "error.main", color: "#fff" };
  }
  if (hours <= 8) {
    return { backgroundColor: "warning.main", color: "#fff" };
  }
  if (hours <= 24) {
    return { backgroundColor: "success.main", color: "#fff" };
  }
  return { backgroundColor: "action.selected", color: "text.primary" };
};

export const filterTasks = (tasks: ProspectTaskDto[], filter: TaskFilter) => {
  return tasks.filter((task) => {
    switch (filter) {
      case "open":
        return task.status !== 4; // Not Done
      case "completed":
        return task.status === 4; // Done
      default:
        return true;
    }
  });
};

export const sortByPriorityAndDueDate = (a: ProspectTaskDto, b: ProspectTaskDto) => {
  // Sort by priority first (1=Urgent to 4=Low)
  const priorityDiff = (a.priority ?? 4) - (b.priority ?? 4);
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  // Within same priority, sort by due date (tasks without due dates go to the end)
  const aMillis = a.dueAt ? DateTime.fromISO(a.dueAt).toMillis() : Number.MAX_SAFE_INTEGER;
  const bMillis = b.dueAt ? DateTime.fromISO(b.dueAt).toMillis() : Number.MAX_SAFE_INTEGER;
  return aMillis - bMillis;
};

export const sortTasks = (tasks: ProspectTaskDto[], filter: TaskFilter, sortNewestFirst: boolean) => {
  // First filter the tasks, then sort them
  const filteredTasks = filterTasks(tasks, filter);
  return filteredTasks.sort((a, b) => {
    if (filter === "completed") {
      // Sort completed tasks by completion date (newest first when sortNewestFirst is true)
      const aMillis = DateTime.fromISO(a.completedAt ?? "").toMillis();
      const bMillis = DateTime.fromISO(b.completedAt ?? "").toMillis();
      return sortNewestFirst ? bMillis - aMillis : aMillis - bMillis;
    }

    if (filter === "all") {
      // For "all" tab: completed tasks go last, then sort by priority and due date
      const aCompleted = a.status === 4;
      const bCompleted = b.status === 4;

      if (aCompleted !== bCompleted) {
        return aCompleted ? 1 : -1; // Push completed tasks to the end
      }

      if (aCompleted) {
        // Both are completed, sort by completion date (newest first)
        return DateTime.fromISO(b.completedAt ?? "").toMillis() - DateTime.fromISO(a.completedAt ?? "").toMillis();
      }
    }

    // For open tasks and non-completed tasks in "all" tab
    return sortByPriorityAndDueDate(a, b);
  });
};
